import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { NotesService } from '../../services/notes.service'
import { AircraftNote } from '../../types/database'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../hooks/useProfile'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import EditTimeCountdown from '../market/EditTimeCountdown'

interface AircraftNotesProps {
  aircraftId: number
  modelId: number
}

type NoteWithAuthor = AircraftNote & { author_name: string }

export default function AircraftNotes({ aircraftId, modelId }: AircraftNotesProps) {
  const { user } = useAuth()
  const { profile } = useProfile()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _location = useLocation()
  const [notes, setNotes] = useState<NoteWithAuthor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Unified notes URL - always use aircraft ID regardless of which section we're in
  const getNotesUrl = () => {
    return `/market/${modelId}/aircraft/${aircraftId}/notes`
  }

  useEffect(() => {
    fetchRecentNotes()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftId])

  const fetchRecentNotes = async () => {
    try {
      setLoading(true)
      setError(null)
      const recentNotes = await NotesService.getRecentByAircraftIdWithAuthors(aircraftId, 5)
      setNotes(recentNotes)
    } catch (err: any) {
      // Check if it's a table not found error (database not migrated yet)
      if (err.message?.includes('relation "aircraft_notes" does not exist') ||
          err.message?.includes('table "aircraft_notes" does not exist')) {
        setError('Notes table not found. Please run the database migration.')
      } else {
        setError('Failed to load notes')
      }
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const isAdmin = profile?.account_type === 'admin' || profile?.account_type === 'global_admin'

  const canEditNote = (note: NoteWithAuthor): boolean => {
    if (!user) return false
    return NotesService.canEditNote(note, user.id, isAdmin)
  }

  if (loading) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
        <div className="text-center py-4">
          <div className="text-sm text-gray-500">Loading notes...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
        {notes.length > 0 && (
          <div className="flex space-x-2">
            <Link
              to={getNotesUrl()}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              View All Notes
            </Link>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-sm text-gray-500 mb-3">No notes yet for this aircraft</div>
          <Link
            to={getNotesUrl()}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Add First Note
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div
              key={note.id}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-150"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="text-sm text-gray-500">
                  {formatDate(note.created_at)} <span className="font-semibold text-indigo-600">{note.author_name}</span>
                </div>
                {canEditNote(note) && (
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`${getNotesUrl()}?edit=${note.id}`}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Edit
                    </Link>
                    {!isAdmin && (
                      <EditTimeCountdown
                        createdAt={note.created_at}
                        className="ml-1"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-900 leading-relaxed">
                {note.content.length > 200 ? (
                  <>
                    {note.content.substring(0, 200)}...
                    <Link
                      to={getNotesUrl()}
                      className="text-indigo-600 hover:text-indigo-800 ml-1 font-medium"
                    >
                      Read more
                    </Link>
                  </>
                ) : (
                  note.content
                )}
              </div>
            </div>
          ))}

          {notes.length >= 5 && (
            <div className="text-center pt-2">
              <Link
                to={getNotesUrl()}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                View All Notes (showing 5 most recent)
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
